import {
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownPopover,
  DropdownMenu,
  DropdownItem,
  AvatarImage,
  AvatarFallback,
  AvatarRoot,
} from '@heroui/react';
import {
  Columns2,
  Sun,
  Moon,
  Monitor,
  LogOut,
  User,
  LayoutGrid,
  Menu,
  Search,
  Sparkles,
} from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { useTheme } from 'next-themes';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useGlobalSearch } from '@/components/search/useGlobalSearch';
import { useAiAssistant } from '@/contexts/AiAssistantContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSidebar } from '@/contexts/SidebarContext';
import { APP_REGISTRY, WORKSPACE_PATH } from '@/lib/apps';
import { lazyModule } from '@/lib/lazyModule';
import { useActiveApp } from '@/lib/useActiveApp';
import { useAfterFirstPaint } from '@/lib/useAfterFirstPaint';
import { useIsDesktop } from '@/lib/useMediaQuery';

// The bar's live parts — unread messages, the bell, the search palette — load
// and subscribe after the page has painted (useAfterFirstPaint): they fetched,
// opened channels and pulled in motion in the same tasks as the screen being
// opened. Their places are held at their size meanwhile, so nothing moves.
const ChatLauncher = lazyModule(() => import('@/components/chat/ChatLauncher').then((m) => ({ default: m.ChatLauncher })));
const NotificationBell = lazyModule(() => import('@/components/notifications/NotificationBell').then((m) => ({ default: m.NotificationBell })));
const SearchDialog = lazyModule(() => import('@/components/search/SearchDialog').then((m) => ({ default: m.SearchDialog })));
const BAR_SLOT = <span aria-hidden className="inline-block h-8 w-8 max-sm:h-10 max-sm:w-10" />;

/** The three theme choices, in the order they are shown. */
const THEME_CHOICES = [
  { key: 'light', icon: Sun, labelKey: 'nav.light' },
  { key: 'dark', icon: Moon, labelKey: 'nav.dark' },
  { key: 'system', icon: Monitor, labelKey: 'nav.system' },
] as const;

const LANGS = [
  { key: 'en', label: 'English' },
  { key: 'fr', label: 'Français' },
];

export function TopNav() {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();
  const { isAuthenticated, currentUser, logout, updateUser } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    void navigate('/login');
  };

  const handleLanguageChange = (key: string) => {
    void i18n.changeLanguage(key);
    localStorage.setItem('i18n_lang', key);
    if (isAuthenticated && currentUser?.id) {
      updateUser(currentUser.id, { user: { lang: key } }).catch(() => {
        /* swallow — local i18n change already applied */
      });
    }
  };

  const handleThemeChange = (key: string) => {
    setTheme(key);
    if (isAuthenticated && currentUser?.id) {
      updateUser(currentUser.id, { user: { theme: key } }).catch(() => {
        /* swallow — local theme change already applied */
      });
    }
  };

  const { navItems, toggle } = useSidebar();
  const { launcherKey } = useActiveApp();
  // At phone widths the nav's right cluster is already full; the Workspace
  // moves into the launcher grid there. Decided in JS, like the dock.
  const isDesktop = useIsDesktop();
  const aiAssistant = useAiAssistant();
  // The idle AI button shimmers; nobody who asked for less motion gets it.
  const reduceMotion = useReducedMotion();
  const [appsOpen, setAppsOpen] = useState(false);
  const search = useGlobalSearch();
  const shellReady = useAfterFirstPaint();
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform);

  const initials = currentUser
    ? `${currentUser.firstname[0]}${currentUser.lastname[0]}`.toUpperCase()
    : '?';

  const logoUrl = (document.getElementById('app') as HTMLDivElement | null)?.dataset.logoUrl ?? '';

  // The bar is deliberately not backdrop-blurred: its background is opaque, so
  // the blur changed nothing on screen and only cost a compositing pass on
  // every frame — paid while scrolling any page, phones included.

  return (
    <nav className="border-border sticky top-0 z-50 w-full border-b bg-background">
      <div className="container mx-auto flex h-14 max-w-7xl items-center justify-between px-4">
        {/* Left: hamburger (mobile, only when an app sidebar is active) + logo */}
        <div className="flex items-center gap-2">
          {navItems.length > 0 && (
            <button
              type="button"
              aria-label={t('nav.toggleSidebar')}
              onClick={toggle}
              className="text-muted-foreground hover:bg-muted hover:text-foreground inline-flex h-8 w-8 max-sm:h-10 max-sm:w-10 items-center justify-center rounded-md transition-colors md:hidden"
            >
              <Menu size={20} />
            </button>
          )}
          <Link
            to="/"
            className="flex items-center gap-2 text-xl font-bold text-primary transition-opacity hover:opacity-80"
          >
            {logoUrl && (
              <img
                src={logoUrl}
                alt="MultiMagic"
                className="h-7 w-7 shrink-0 rounded-lg object-contain sm:h-8 sm:w-8"
              />
            )}
            {/* On phones the six actions get 40px targets and the name yields
                to the logo — the brand is the icon there. */}
            <span className="max-sm:hidden">MultiMagic</span>
          </Link>
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-1">
          {/* Global search */}
          {isAuthenticated && (
            <button
              type="button"
              onClick={search.open}
              aria-label={t('search.open')}
              className="text-muted-foreground hover:text-accent-foreground inline-flex h-8 max-sm:h-10 max-sm:w-10 max-sm:justify-center max-sm:px-0 items-center gap-2 rounded-md px-2 hover:bg-accent"
            >
              <Search size={18} />
              <kbd className="font-mono text-[10px] max-sm:hidden sm:inline">
                {isMac ? '⌘K' : 'Ctrl K'}
              </kbd>
            </button>
          )}

          {/* Workspace — several apps on one screen (phones: see the launcher grid) */}
          {isAuthenticated && isDesktop && (
            <Link
              to={WORKSPACE_PATH}
              aria-label={t('workspace.title')}
              title={t('workspace.title')}
              aria-current={launcherKey === 'workspace' ? 'page' : undefined}
              className={`inline-flex h-8 max-sm:h-10 max-sm:w-10 max-sm:justify-center max-sm:px-0 items-center gap-1.5 rounded-md px-2 transition-colors ${
                launcherKey === 'workspace'
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-accent-foreground hover:bg-accent'
              }`}
            >
              <Columns2 size={18} />
              {/* Label from lg up — the nav is tight on smaller screens.
                  `max-lg:hidden`, not `hidden lg:inline`: see the note in
                  docs/MULTI_APP_WORKSPACE.md §15 (Phase 2 corrections). */}
              <span className="text-xs font-medium max-lg:hidden">{t('workspace.title')}</span>
            </Link>
          )}

          {/* What the app has told you (docs/NOTIFICATIONS.md). */}
          {/* Messages before notifications: a chat is the thing you answer,
              the bell is the thing you read. */}
          {isAuthenticated && (
            <Suspense fallback={BAR_SLOT}>{shellReady ? <ChatLauncher /> : BAR_SLOT}</Suspense>
          )}

          {isAuthenticated && (
            <Suspense fallback={BAR_SLOT}>{shellReady ? <NotificationBell /> : BAR_SLOT}</Suspense>
          )}

          {/* AI assistant — global: opens over whatever page you are on.
              Closed, it is the one thing in this bar worth inviting a click:
              a tinted, borderless pill whose sparkle gently turns and pulses,
              rather than another grey icon (owner: no border, it should move).
              Open, it fills with the primary colour and stands still, so the
              two states are told apart by more than a tint. */}
          {isAuthenticated && (
            <button
              onClick={aiAssistant.toggle}
              aria-label={t('ai.title')}
              title={t('ai.title')}
              aria-pressed={aiAssistant.state === 'open'}
              className={`relative inline-flex h-8 w-8 max-sm:h-10 max-sm:w-10 items-center justify-center overflow-hidden rounded-md transition-colors ${
                aiAssistant.state === 'open'
                  ? 'text-primary-foreground bg-primary shadow-sm'
                  : 'text-primary bg-primary/10 hover:bg-primary/20'
              }`}
            >
              {aiAssistant.state !== 'open' && !reduceMotion && (
                <motion.span
                  aria-hidden
                  className="absolute inset-0 rounded-md bg-primary/25"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 0.55, 0] }}
                  transition={{ duration: 2.8, repeat: Infinity, repeatDelay: 3.2, ease: 'easeInOut' }}
                />
              )}
              {aiAssistant.state !== 'open' && !reduceMotion ? (
                <motion.span
                  aria-hidden
                  className="relative inline-flex"
                  animate={{ rotate: [0, -12, 12, 0], scale: [1, 1.15, 1.15, 1] }}
                  transition={{ duration: 1.4, repeat: Infinity, repeatDelay: 4.6, ease: 'easeInOut' }}
                >
                  <Sparkles size={18} />
                </motion.span>
              ) : (
                <Sparkles size={18} className="relative" />
              )}
            </button>
          )}

          {/* Apps launcher */}
          {isAuthenticated && (
            <Dropdown isOpen={appsOpen} onOpenChange={setAppsOpen}>
              <DropdownTrigger
                aria-label={t('nav.apps')}
                className="hover:text-accent-foreground inline-flex h-8 w-8 max-sm:h-10 max-sm:w-10 items-center justify-center rounded-md hover:bg-accent"
              >
                <LayoutGrid size={18} />
              </DropdownTrigger>
              {/* A phone gets three wider columns; four 57px columns made
                  "Workspace" and "AI Assistant" overrun their tiles and
                  collide with the next one. */}
              <DropdownPopover className="w-[19rem] max-w-[calc(100vw_-_1.5rem)] p-3 sm:w-72">
                <AnimatePresence>
                  {appsOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.15 }}
                    >
                      <p className="text-muted-foreground mb-2 px-1 text-xs font-semibold uppercase tracking-wider">
                        {t('nav.apps')}
                      </p>
                      <motion.div
                        className="grid grid-cols-3 gap-1.5 sm:grid-cols-4"
                        initial="hidden"
                        animate="show"
                        variants={{
                          hidden: {},
                          show: { transition: { staggerChildren: 0.03, delayChildren: 0.05 } },
                        }}
                      >
                        {/* Desktop: the workspace has its own nav button, so no tile.
                            Phones: the nav has no room, so it is a tile here. */}
                        {APP_REGISTRY.filter((app) =>
                          isDesktop ? app.key !== 'workspace' : true,
                        ).map(({ key, titleKey, icon: Icon, path, color, available }) => {
                          const active = available && launcherKey === key;
                          return (
                            <motion.button
                              key={key}
                              type="button"
                              disabled={!available}
                              variants={{
                                hidden: { opacity: 0, scale: 0.8, y: 6 },
                                show: { opacity: 1, scale: 1, y: 0 },
                              }}
                              transition={{ type: 'spring', stiffness: 380, damping: 22 }}
                              whileHover={available ? { scale: 1.06 } : undefined}
                              whileTap={available ? { scale: 0.94 } : undefined}
                              onClick={() => {
                                if (!available) return;
                                setAppsOpen(false);
                                void navigate(path);
                              }}
                              aria-current={active ? 'page' : undefined}
                              className={`flex min-w-0 flex-col items-center gap-1 rounded-lg p-2 transition-colors ${
                                available
                                  ? active
                                    ? 'bg-primary/10 ring-1 ring-primary/30'
                                    : 'cursor-pointer hover:bg-accent'
                                  : 'cursor-not-allowed opacity-40'
                              }`}
                              title={!available ? t('nav.comingSoon') : undefined}
                            >
                              <div
                                className={`flex h-9 w-9 items-center justify-center rounded-lg max-sm:h-10 max-sm:w-10 ${
                                  active ? `bg-primary/15 ${color}` : `bg-muted ${color}`
                                }`}
                              >
                                <Icon size={16} />
                              </div>
                              {/* Two lines, wrapped inside the tile and the same
                                  height on every row, so the labels line up
                                  instead of overlapping. */}
                              <span
                                className={`line-clamp-2 min-h-[2rem] w-full break-words text-center text-[11px] leading-tight ${
                                  active ? 'font-semibold text-primary' : 'text-muted-foreground'
                                }`}
                              >
                                {t(titleKey)}
                              </span>
                            </motion.button>
                          );
                        })}
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </DropdownPopover>
            </Dropdown>
          )}

          {/* Auth */}
          {isAuthenticated ? (
            <Dropdown>
              <DropdownTrigger
                aria-label={t('nav.account')}
                className="ml-1 rounded-full max-sm:p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <AvatarRoot className="h-8 w-8 cursor-pointer">
                  {currentUser?.avatar ? (
                    <AvatarImage src={currentUser.avatar} alt={initials} />
                  ) : null}
                  <AvatarFallback className="text-primary-foreground bg-primary text-xs font-medium">
                    {initials}
                  </AvatarFallback>
                </AvatarRoot>
              </DropdownTrigger>
              <DropdownPopover className="w-60">
                <DropdownMenu>
                  <DropdownItem
                    id="profile"
                    key="profile"
                    onAction={() => void navigate('/profile/me')}
                  >
                    <span className="flex items-center gap-2">
                      <User size={14} />
                      {t('nav.profile')}
                    </span>
                  </DropdownItem>
                  <DropdownItem
                    id="logout"
                    key="logout"
                    onAction={handleLogout}
                    className="text-danger"
                  >
                    <span className="flex items-center gap-2">
                      <LogOut size={14} />
                      {t('auth.logout')}
                    </span>
                  </DropdownItem>
                </DropdownMenu>

                {/* Theme and language live here rather than as two more icons
                    in the bar: they are settings, picked once, and the bar is
                    for what you reach for all day. Buttons rather than menu
                    items, so choosing one does not close the menu — you can
                    watch the theme change and pick again. */}
                <div className="border-border/70 mt-1 border-t px-2 pb-1 pt-2">
                  <p className="text-muted-foreground px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide">
                    {t('nav.theme')}
                  </p>
                  <div className="flex gap-1" role="group" aria-label={t('nav.theme')}>
                    {THEME_CHOICES.map(({ key, icon: Icon, labelKey }) => (
                      <button
                        key={key}
                        type="button"
                        aria-pressed={theme === key}
                        onClick={() => handleThemeChange(key)}
                        className={`flex flex-1 flex-col items-center gap-1 rounded-lg border px-1 py-1.5 text-[11px] transition-colors ${
                          theme === key
                            ? 'border-primary bg-primary/10 text-primary font-semibold'
                            : 'border-border text-muted-foreground hover:bg-accent'
                        }`}
                      >
                        <Icon size={14} />
                        {t(labelKey)}
                      </button>
                    ))}
                  </div>

                  <p className="text-muted-foreground px-1 pb-1.5 pt-3 text-[11px] font-semibold uppercase tracking-wide">
                    {t('nav.language')}
                  </p>
                  <div className="flex gap-1" role="group" aria-label={t('nav.language')}>
                    {LANGS.map((lang) => (
                      <button
                        key={lang.key}
                        type="button"
                        aria-pressed={i18n.language === lang.key}
                        onClick={() => handleLanguageChange(lang.key)}
                        className={`flex-1 rounded-lg border px-2 py-1.5 text-xs transition-colors ${
                          i18n.language === lang.key
                            ? 'border-primary bg-primary/10 text-primary font-semibold'
                            : 'border-border text-muted-foreground hover:bg-accent'
                        }`}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                </div>
              </DropdownPopover>
            </Dropdown>
          ) : (
            <div className="ml-2 flex items-center gap-2">
              <Link to="/about" className="max-sm:hidden">
                <Button variant="ghost" size="sm">
                  {t('nav.about')}
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost" size="sm">
                  {t('auth.login')}
                </Button>
              </Link>
              <Link to="/signup">
                <Button variant="primary" size="sm">
                  {t('auth.signup')}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
      {/* Opened before the page went idle (Ctrl+K at once), it loads then. */}
      {isAuthenticated && (shellReady || search.isOpen) && (
        <Suspense fallback={null}>
          <SearchDialog isOpen={search.isOpen} onClose={search.close} />
        </Suspense>
      )}
    </nav>
  );
}
